import {
  TextFieldProps,
  ClickAwayListener,
  CircularProgress,
} from '@mui/material'
import classNames from 'classnames'
import {
  useAddressState,
  AddressSliceKey,
} from 'feature/addresses/hooks/useAddressState'
import {
  SavedAddress,
  useGetAddressListQuery,
} from 'feature/addresses/redux/MyAddresses.api'
import { useIsAuth } from 'feature/auth/hooks'
import { TSuggest } from 'feature/registration/types'
import { useSetNotice } from 'hooks/useSetNotice'
import { useState, useRef, ChangeEvent, RefObject } from 'react'
import { Button, TextField } from 'ui'
import { Severity } from 'widget/Notifications'

import style from './DadataSelectInput.module.scss'
import { useHandlers } from './hooks/useHandlers'
import { UserAddressButton } from './userAddressButton/UserAddressButton'

export type TDadataSelectInput = {
  inputProps: TextFieldProps
  className?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onSelectStart?: () => void
  onClick?: (ref: RefObject<HTMLDivElement>) => void
  onSelect?: (s: TSuggest) => void
  onClickAway?: (e: MouseEvent | TouchEvent) => void
  onEmptyData?: () => void
  clearErrorsAddress: () => void
  setErrorAddress: (type: string, message: string) => void
  userAddressClick?: (address: SavedAddress) => void
  isUserAddressHidden?: boolean
  addressKey?: AddressSliceKey
}

export function DadataSelectInput(props: TDadataSelectInput) {
  const [isDadataSelectVisible, setDadataSelectVisible] = useState(false)
  const [isUserAddressVisible, setUserAddressVisible] = useState<boolean>(false)
  const isAuth = useIsAuth()
  const setNotice = useSetNotice()

  const {
    inputProps,
    className,
    onClick,
    userAddressClick,
    isUserAddressHidden,
    addressKey = 'default',
  } = props

  const { data: addressesData, isLoading } = useGetAddressListQuery(undefined, {
    skip: !isAuth,
  })
  const { suggests, isLoadingSuggest, input } = useAddressState(addressKey)

  const selectInputRef = useRef<HTMLInputElement | null>(null)
  const selectInputRefInMui = useRef<HTMLInputElement | null>(null)

  const addError = () => {
    setNotice('Ошибка сервера', Severity.error)
  }

  const { handleChange, selectHandler, onClickAwayHandler, handleClick } =
    useHandlers({
      props,
      setUserAddressVisible,
      setDadataSelectVisible,
      addressesData,
      isLoading,
      isDadataSelectVisible,
      selectInputRefInMui,
      addError,
      addressKey,
    })

  return (
    <ClickAwayListener onClickAway={onClickAwayHandler}>
      <div
        onClick={() => {
          if (onClick) onClick(selectInputRef)
        }}
        className={style.select}
      >
        {isLoadingSuggest && (
          <CircularProgress className={style.loader} size={25} />
        )}
        <TextField
          autoComplete='hard-off-city'
          className={classNames(style.selectInput, className && className)}
          ref={selectInputRef}
          inputRef={selectInputRefInMui}
          onFocus={handleClick}
          onChange={handleChange}
          value={input}
          {...inputProps}
        />
        <div
          className={classNames(
            style.selectList,
            isDadataSelectVisible && style.selectListVisible,
          )}
        >
          <ul>
            {suggests?.map(item => (
              <Button
                key={item.address} // fiasId может повторяться в списке дадаты
                color='primary'
                variant='text'
                className={style.selectItem}
                onClick={() => selectHandler(item)}
                data-testid='dadata-suggest-item'
              >
                {item.address}
              </Button>
            ))}
          </ul>
        </div>

        {!!addressesData?.list.length && !isUserAddressHidden && (
          <div
            className={classNames(style.personalAddress, {
              [style.isVisible]: isUserAddressVisible,
            })}
          >
            {addressesData.list.map(item => (
              <UserAddressButton
                key={item.fiasId}
                userAddressClick={userAddressClick}
                item={item}
                setUserAddressVisible={setUserAddressVisible}
              />
            ))}
          </div>
        )}
      </div>
    </ClickAwayListener>
  )
}
